
<script>
import { mapGetters } from 'vuex';
import tailwindConfig from '@/tailwind.config.js';
import ProductCustomisationCanvas from '@/components/ProductCustomisationCanvas.vue';
import Carousel from '@/components/Carousel.vue';
import ProductVideo from '@/components/ProductVideo.vue';

import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css';

export default {
    components: {
        ProductCustomisationCanvas,
        Carousel,
        ProductVideo,
    },

    props: {
        images: {
            type: Array,
            required: true,
        },
        videos: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            activeImageIndex: 0,
            hideWrapperFadeStart: false,
            hideWrapperFadeEnd: false,
            hideWrapperFadeStartMobile: false,
            hideWrapperFadeEndMobile: false,
            activeVideoPlayer: null,
        };
    },

    computed: {
        ...mapGetters('product', [
            'showProductCustomisationImage',
            'activeVariationImage',
        ]),

        mediaItems() {
            const result = [];
            
            // Always add the first image
            if (this.images.length > 0) {
                result.push({
                    type: 'image',
                    data: this.images[0],
                    originalIndex: 0,
                });
            }
            
            // Add all videos after the first image, sorted by their position
            const sortedVideos = [...this.videos].sort((a, b) => (a.position || 0) - (b.position || 0));
            sortedVideos.forEach((video) => {
                result.push({
                    type: 'video',
                    data: video,
                    originalIndex: null,
                });
            });
            
            // Add remaining images after videos
            for (let i = 1; i < this.images.length; i++) {
                result.push({
                    type: 'image',
                    data: this.images[i],
                    originalIndex: i,
                });
            }
            
            return result;
        },

        isMobile: {
            get() {
                return typeof window !== 'undefined' && window.innerWidth < parseInt(tailwindConfig.theme.screens.md, 10);
            },
            cache: false,
        },
    },

    mounted() {
        if (this.isMobile) {
            this.showHideThumbnailScrollFadeMobile();
        } else {
            this.showHideThumbnailScrollFade();
        }
    },

    methods: {
        showHideThumbnailScrollFadeMobile() {
            const { wrapperMobile } = this.$refs;
            if (wrapperMobile) {
                this.hideWrapperFadeStartMobile = wrapperMobile.scrollLeft === 0;
                this.hideWrapperFadeEndMobile = wrapperMobile.clientWidth + wrapperMobile.scrollLeft === wrapperMobile.scrollWidth;
            }
        },
        showHideThumbnailScrollFade() {
            const { wrapper } = this.$refs;
            if (wrapper) {
                this.hideWrapperFadeStart = wrapper.scrollTop === 0;
                this.hideWrapperFadeEnd = wrapper.clientHeight + wrapper.scrollTop === wrapper.scrollHeight;
            }
        },
        isImageActive(index) {
            return this.activeImageIndex === index;
        },
        setActiveImageIndex(index) {
            this.$store.dispatch('product/setShowProductCustomisationImage', false);
            this.$store.dispatch('product/setActiveVariationImage', null);
            
            this.activeImageIndex = index;
        },
        pauseActiveVideo() {
            // Find all video components and pause them
            this.mediaItems.forEach((mediaItem, index) => {
                if (mediaItem.type === 'video') {
                    const videoComponent = this.$refs[`media-${index}`];
                    if (videoComponent && videoComponent[0] && videoComponent[0].pause) {
                        videoComponent[0].pause();
                    }
                }
            });
            this.activeVideoPlayer = null;
        },
        handleVideoPlayback(index) {
            const activeMediaItem = this.mediaItems[index];
            if (activeMediaItem && activeMediaItem.type === 'video') {
                // The ProductVideo component handles play/pause based on isActive prop
                // No additional logic needed here as the reactivity handles it
            }
        },
        updateCanvasPreview(image) {
            this.$store.dispatch('product/setCanvasPreview', image);
        },
        formatDuration(seconds) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
        },
        handleVideoReady(index, player) {
            // Store reference to the video player if this is the active slide
            if (this.isImageActive(index)) {
                this.activeVideoPlayer = player;
            }
        },
    },

};
</script>

<template>
    <client-only>
        <div class="block lg:flex">

            <div
                v-if="mediaItems.length > 1"
                class="relative mr-3 hidden md:block w-1/6 flex-shrink-0 flex-grow-0 basis-1/6 thumbnails-container"
            >
                <div
                    ref="wrapper"
                    class="thumbnails flex flex-col gap-4 overflow-y-scroll"
                    @scroll="showHideThumbnailScrollFade"
                >
                    <button
                        v-for="(mediaItem, index) in mediaItems"
                        :key="index"
                        class="relative"
                    >
                        <img
                            v-if="mediaItem.type === 'image'"
                            v-lazy="mediaItem.data.sizes.small"
                            :alt="mediaItem.data.alt"
                            :class="{ 'opacity-50': !isImageActive(index) }"
                            class="thumbnails__image mb-3"
                            @click="setActiveImageIndex(index)"
                        >
                        <div
                            v-else-if="mediaItem.type === 'video'"
                            class="relative cursor-pointer"
                            @click="setActiveImageIndex(index)"
                        >
                            <img
                                :src="mediaItem.data.thumbnail_url"
                                :alt="mediaItem.data.title || 'Video'"
                                :class="{ 'opacity-50': !isImageActive(index) }"
                                class="thumbnails__image mb-3"
                            >
                            <!-- Play button overlay -->
                            <div class="absolute inset-0 flex items-center justify-center">
                                <div class="bg-black bg-opacity-50 rounded-full p-2">
                                    <svg
                                        class="w-6 h-6 text-white"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                    </svg>
                                </div>
                            </div>
                            <!-- Duration badge -->
                            <div
                                v-if="mediaItem.data.duration"
                                class="absolute bottom-1 right-1 bg-black bg-opacity-75 text-white text-xs px-1 rounded"
                            >
                                {{ formatDuration(mediaItem.data.duration) }}
                            </div>
                        </div>
                    </button>

                </div>
            </div>

            <div class="md:w-5/6 flex-shrink-0 flex-grow-0 basis-5/6">
                <ProductCustomisationCanvas
                    v-if="showProductCustomisationImage && !activeVariationImage"
                    @change="updateCanvasPreview($event)"
                />
                <template v-else-if="activeVariationImage">
                    <img
                        :src="activeVariationImage"
                        class="image"
                    >
                </template>
                <Carousel
                    v-else-if="mediaItems.length > 0"
                    ref="carousel"
                    :arrows="true"
                    :dots="true"
                    :active-slide="activeImageIndex"
                    class="w-full"
                    :slides-count="mediaItems.length"
                    @afterChange="setActiveImageIndex"
                >
                    <div
                        v-for="(mediaItem, index) in mediaItems"
                        :key="index"
                    >
                        <img
                            v-if="mediaItem.type === 'image'"
                            :src="mediaItem.data.sizes.large"
                            class="image"
                            :alt="mediaItem.data.alt || 'Product image'"
                        >
                        <div
                            v-else-if="mediaItem.type === 'video'"
                            class="image relative"
                        >
                            <ProductVideo
                                :ref="`media-${index}`"
                                :vimeo-id="mediaItem.data.vimeo_id"
                                :title="mediaItem.data.title"
                                :description="mediaItem.data.description"
                                :is-active="isImageActive(index)"
                                :thumbnail-url="mediaItem.data.thumbnail_url"
                                class="w-full h-full"
                                @video-ready="handleVideoReady(index, $event)"
                            />
                        </div>
                    </div>

                </Carousel>
            </div>
        </div>
    </client-only>
</template>

<style lang="scss" scoped>

.thumbnails {
    @apply relative;
    max-height: 600px;
    max-width: 600px;
    &__image {
        @apply block rounded cursor-pointer transition-fast;
        object-fit: cover;
        object-position: center;
        &:last-child {
            @apply mb-0;
        }
        &:hover {
            @apply opacity-100;
        }
    }
    &__scrollfade {
        @apply absolute pointer-events-none transition-slow z-10;
        &--top {
            top: 0;
            background: linear-gradient(
                180deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(255, 255, 255, 1) 25%,
                rgba(0, 0, 0, 0) 100%
            );
            @apply w-full h-16;
        }
        &--bottom {
            bottom: 0;
            background: linear-gradient(
                0deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(255, 255, 255, 1) 25%,
                rgba(0, 0, 0, 0) 100%
            );
            @apply w-full h-16;
        }
        &--left {
            bottom: 0;
            left: 0;
            background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(255, 255, 255, 1) 25%,
                rgba(0, 0, 0, 0) 100%
            );
            @apply h-full w-16;
        }
        &--right {
            bottom: 0;
            right: 0;
            background: linear-gradient(
                -90deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(255, 255, 255, 1) 25%,
                rgba(0, 0, 0, 0) 100%
            );
            @apply h-full w-16;
        }
    }
}
.image {
    @apply aspect-square;
}
</style>
